import { PROFILE_PHOTO_MAX_BYTES, type OwnProfile } from '@gb-ticket/shared-types';
import { Loader2Icon, Trash2Icon, UploadIcon } from 'lucide-react';
import { useRef, type ChangeEvent } from 'react';

import { useVoiceCopy } from '@/platform/voice/use-voice';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ProfilePhotoPreview } from '@/platform/features/profile/profile-photo-preview';
import {
  useRemoveProfilePhoto,
  useUploadProfilePhoto,
} from '@/platform/features/users/use-own-profile';
import { ApiRequestError } from '@/lib/api';

const ACCEPT = 'image/jpeg,image/png,image/webp';

export function ProfilePhotoCard({
  profile,
  embedded = false,
}: {
  profile: OwnProfile;
  /** Ohne eigene Karte, als Abschnitt in der Profil-Einstellungen-Karte. */
  embedded?: boolean;
}) {
  const copy = useVoiceCopy();
  const inputRef = useRef<HTMLInputElement>(null);
  const upload = useUploadProfilePhoto();
  const remove = useRemoveProfilePhoto();

  async function onPick(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }
    if (file.size > PROFILE_PHOTO_MAX_BYTES) {
      toast.error('Das Bild darf höchstens 2 MB groß sein.');
      return;
    }
    try {
      await upload.mutateAsync(file);
      toast.success('Profilfoto gespeichert.');
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Upload fehlgeschlagen.');
    }
  }

  async function onRemove() {
    try {
      await remove.mutateAsync();
      toast.success('Profilfoto entfernt.');
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Entfernen fehlgeschlagen.');
    }
  }

  const busy = upload.isPending || remove.isPending;

  const body = (
    <>
      <ProfilePhotoPreview profile={profile} className="size-20 shrink-0" />
      <div className="min-w-0 flex-1 space-y-2">
        <div className="flex flex-wrap gap-2">
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            className="sr-only"
            onChange={(event) => void onPick(event)}
          />
          <Button
            type="button"
            variant="outline"
            disabled={busy}
            onClick={() => inputRef.current?.click()}
          >
            {upload.isPending ? (
              <Loader2Icon className="size-4 animate-spin" aria-hidden />
            ) : (
              <UploadIcon className="size-4" aria-hidden />
            )}
            {profile.profilePhotoUrl ? 'Ersetzen' : 'Hochladen'}
          </Button>
          {profile.profilePhotoUrl ? (
            <Button type="button" variant="ghost" disabled={busy} onClick={() => void onRemove()}>
              {remove.isPending ? (
                <Loader2Icon className="size-4 animate-spin" aria-hidden />
              ) : (
                <Trash2Icon className="size-4" aria-hidden />
              )}
              Entfernen
            </Button>
          ) : null}
        </div>
        {embedded ? (
          <p className="text-xs text-muted-foreground">
            Profilfoto wird beim Hochladen oder Entfernen sofort gespeichert.
            {profile.profilePhotoUrl ? ` ${copy('platform.profile.photo.zoom')}` : null}
          </p>
        ) : null}
      </div>
    </>
  );

  if (embedded) {
    return <div className="flex flex-col gap-4 sm:flex-row sm:items-center">{body}</div>;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Profilfoto</CardTitle>
        <CardDescription>
          JPEG, PNG oder WebP, höchstens 2 MB. Sichtbar für angemeldete Kolleginnen und Kollegen.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4 sm:flex-row sm:items-center">{body}</CardContent>
    </Card>
  );
}
